import { isNullOrUndefined } from "@clipboard-health/util-ts";
import { z } from "zod";

import { API_TYPES } from "../api";
import { type Address } from "../lib/GoogleMaps";
import {
  BAN_DISPUTE_REASON,
  BAN_DISPUTE_STATUS,
} from "../PlacementCandidateBans/api/useFetchPaginatedPlacementCandidateBans";

// eslint-disable-next-line no-restricted-syntax
export enum PlacementCandidateStatus {
  DISABLED = "disabled",
  ONBOARDING = "onboarding",
  PENDING = "pending",
  ACTIVATED = "activated",
}

// eslint-disable-next-line no-restricted-syntax
export enum PlacementCandidateScreeningStatus {
  NO_SCREENING = "no_screening",
  PENDING = "pending",
  PASSED = "passed",
  PASSED_EXCEPTIONALLY = "passed:exceptionally",
  FAILED = "failed",
}

// eslint-disable-next-line no-restricted-syntax
export enum JobType {
  FULL_TIME = "full_time",
  PART_TIME = "part_time",
  PER_DIEM = "per_diem",
  WEEKEND_WARRIOR = "weekend_warrior",
}

// eslint-disable-next-line no-restricted-syntax
export enum ShiftNameType {
  AM = "am",
  PM = "pm",
  NOC = "noc",
}

export interface CredentialType {
  id: string;
  type: "license" | "document";
  name: string;
  isRequired?: boolean;
}

export const placementGeoLocation = z.object({
  coordinates: z.tuple([z.number(), z.number()]),
  type: z.literal("Point"),
});

export type PlacementGeoLocation = z.infer<typeof placementGeoLocation>;

export interface PreferredCandidateArea {
  location?: PlacementGeoLocation;
  address: Address;
  distance: number;
}

// this include is being phased out, but still supplied by the backend
// for backwards compatibility with the mobile app. we leave it here to
// prevent zod schema validation errors  (unexpected 'type' value in union)
export const placementCandidateUploadedResumeSchema = z.object({
  type: z.literal("uploaded-resume"),
  id: z.string(),
  attributes: z.unknown(),
});

export const PlacementCandidateScreeningClipUploadSchema = z.object({
  fileContent: z.string().describe("Base64 encoded audio file content"),
  fileName: z.string().describe("Original filename of the audio file"),
  lengthInSeconds: z.number(),
  transcript: z.string(),
  fileStorage: z
    .object({
      fileKey: z.string(),
      rootFolder: z.string(),
    })
    .optional(),
});

export const PlacementCandidateScreeningClipSchema = z.object({
  fileDistributionUrl: z.string().optional(),
  lengthInSeconds: z.number(),
  transcript: z.string(),
  fileStorage: z
    .object({
      fileKey: z.string(),
      rootFolder: z.string(),
    })
    .optional(),
});

export type PlacementCandidateUploadedResume = z.infer<
  typeof placementCandidateUploadedResumeSchema
>;

export type PlacementCandidateScreeningClipUpload = z.infer<
  typeof PlacementCandidateScreeningClipUploadSchema
>;

export type PlacementCandidateScreeningClip = z.infer<typeof PlacementCandidateScreeningClipSchema>;

export const placementCandidateIncludedBanSchema = z.object({
  type: z.literal(API_TYPES.placementCandidateBan),
  id: z.string(),
  attributes: z.object({
    reason: z.string(),
    expiresAt: z.string().datetime().optional(),
    revokedAt: z.string().datetime().optional(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    dispute: z
      .object({
        reason: z.nativeEnum(BAN_DISPUTE_REASON),
        description: z.string(),
        status: z.nativeEnum(BAN_DISPUTE_STATUS),
        resolution: z.string().optional(),
        resolvedAt: z.string().datetime().optional(),
      })
      .optional(),
  }),
});

export interface PlacementCandidate {
  id: string;
  accessId: string;
  status: PlacementCandidateStatus;
  screeningStatus?: PlacementCandidateScreeningStatus;
  introductionUrl?: string;
  summary?: string;
  distance?: number;
  isExcluded?: boolean;
  attendanceScore: number;
  exclusion?: {
    id: string;
    reason: string;
  };
  preferredAreas?: PreferredCandidateArea[];
  preferredPayRate?: {
    amountInMinorUnits: number;
    currencyCode: string;
  };
  hasAppliedToWorkplace?: boolean;
  resumeUrl?: string;
  passportUrl?: string;
  legacyResumeUrl?: string;
  legacyPendingResumeUrl?: string;
  screeningCallDate?: Date;
  worker: {
    id: string;
    name: string;
    qualification?: string;
    completedShiftsCount?: number;
    documents?: Array<{ id: string; name: string }>;
    licenses?: Array<{
      id: string;
      multiState: boolean;
      state: string;
      qualification: string;
    }>;
    email?: string;
    phone?: string;
    profileImageUrl?: string;
  };
  screeningClips?: PlacementCandidateScreeningClip[];
}

export const placementCandidateFormSchema = z.object({
  status: z.nativeEnum(PlacementCandidateStatus, {
    required_error: "Status is required",
  }),
  screeningStatus: z.nativeEnum(PlacementCandidateScreeningStatus, {
    required_error: "Screening status is required",
  }),
  introductionUrl: z
    .string()
    .optional()
    .refine(
      (value) => {
        if (value === "" || isNullOrUndefined(value)) {
          return true;
        }

        try {
          const url = new URL(value);
          const allowedHosts = ["videoask.com", "www.videoask.com"];
          return allowedHosts.includes(url.host);
        } catch {
          return false;
        }
      },
      {
        message: "Please enter a valid VideoAsk link",
      }
    ),
  summary: z
    .string()
    .optional()
    .refine(
      (value) => {
        if (!value || value.trim() === "") {
          return true;
        }

        const lines = value.split("\n").filter((line) => line.trim() !== "");
        return lines.every((line) => /^[*+-]\s/.exec(line.trim()));
      },
      {
        message: "Each line must begin with a bullet point (-, +, or *)",
      }
    ),
  preferredPayRate: z.coerce.number().optional(),
  preferredArea: z
    .object({
      coordinates: z.object({
        latitude: z.number(),
        longitude: z.number(),
      }),
      address: z.object({
        city: z.string().optional(),
        country: z.string().optional(),
        countryCode: z.string().optional(),
        formatted: z.string().optional(),
        line1: z.string().optional(),
        postalCode: z.string().optional(),
        region: z.string().optional(),
        subpremise: z.string().optional(),
        state: z.string().optional(),
        stateCode: z.string().optional(),
        streetName: z.string().optional(),
        streetNumber: z.string().optional(),
        metropolitanStatisticalArea: z.string().optional(),
      }),
    })
    .optional(),
  distance: z.number().min(0).max(150),
  screeningClips: z
    .array(
      z
        .object({
          transcript: z.string().min(1, "Transcript is required"),
          lengthInSeconds: z.number().optional(),
          audioFile: z.instanceof(File).optional(),
          fileStorage: z
            .object({
              fileKey: z.string(),
              rootFolder: z.string(),
            })
            .optional()
            .nullable(),
        })
        .refine(
          (data) =>
            // Either audioFile or fileStorage must be present
            data.audioFile !== undefined || data.fileStorage !== null,
          {
            message: "Audio file is required",
            path: ["audioFile"],
          }
        )
    )
    .optional(),
  screeningCallDate: z.coerce.date().optional(),
});
